import { makeObservable, observable } from 'mobx';
import merge from 'lodash.merge';

import { entityPool, Model } from '../../core/engine';
import { api } from '../../api';
import { ManyToMany, ManyToOne, OneToMany } from '../../core/engine/decorators';
import { catchError } from '../../core/catch-error';

import { Space, Step, Tag, Member, Project, ProjectPriority, Comment } from './';
import { taskGroupingStore } from '../../core/stores/task-store';
import { toastPool } from '../../features/toasts/models/toast-pool';
import { Toast } from '../../features/toasts/models/toast';
import { context } from '../context';

export enum TaskStatus {
  Todo = 'todo',
  InProgress = 'in-progress',
  OnHold = 'on-hold',
  Done = 'done',
  Canceled = 'canceled',
}

export type TaskLevel = 'space' | 'project' | 'step';

export type SaveTaskDto = {
  title: string;
  status: TaskStatus;
  priority?: ProjectPriority;
  dueDate?: string;
  description?: string;
  projectId?: string;
  stepId?: string;
  assigneeId?: string;
  tagsIds?: string[];
};

export type GetTasksDto = {
  role: ('created-by-me' | 'assigned-to-me')[];
  status: TaskStatus[];
  priority: ProjectPriority[];
  projectId: string;
  stepId: string;
};

export class Task extends Model {
  spaceId: string;

  creatorId: string;

  title: string;

  status: TaskStatus;

  priority: ProjectPriority;

  dueDate?: string;

  projectId?: string;

  stepId?: string | null;

  assigneeId?: string | null;

  description?: string;

  @ManyToOne('tasks')
  space: Space;

  @ManyToOne('tasks')
  project?: Project;

  @ManyToOne('tasks')
  step?: Step;

  @ManyToOne('tasks')
  assignee?: Member;

  tagsIds: string[] = [];

  @ManyToMany('tasks')
  tags: Tag[] = [];

  @OneToMany('target')
  comments: Comment[] = [];

  subscribersIds: string[];

  @ManyToMany('subscriptions')
  subscribers: Member[] = []; 

  constructor() {
    super('tasks');

    this.status = TaskStatus.Todo;
    this.priority = ProjectPriority.NO_PRIORITY;

    makeObservable<Task>(this, {
      _id: observable,
      spaceId: observable,
      creatorId: observable,
      title: observable,
      status: observable,
      priority: observable,
      dueDate: observable,
      projectId: observable,
      stepId: observable,
      assigneeId: observable,
      description: observable,
      tagsIds: observable,
      tags: observable,
      comments: observable,
      subscribersIds: observable,
      subscribers: observable,
    });
  }

  static StatusMapper: Record<TaskStatus, { title: string }> = {
    todo: {
      title: 'Todo',
    },
    'on-hold': {
      title: 'On Hold',
    },
    'in-progress': {
      title: 'In Progress',
    },
    canceled: {
      title: 'Canceled',
    },
    done: {
      title: 'Done',
    },
  };

  override async update(values: Partial<Omit<this, '_id'>>): Promise<any> {
    const currentTask = new Task();
    merge(currentTask, this);

    const updatedTask: Partial<Task> = { ...values };

    try {
      merge(this, updatedTask);

      const { data } = await api.patch(`/tasks/${this._id}`, values);

      if (this.projectId && values.assigneeId) {
        const project = Project.getOne(this.projectId)!;
        if (project) project.lastUpdatedAt = new Date().toString();

        if (!project.collaboratorsIds.includes(values.assigneeId!)) {
          project.collaboratorsIds = [...project.collaboratorsIds, values.assigneeId!];
          const assignee = Member.getOne(values.assigneeId!);
          if (assignee) {
            this.assignee = assignee;
            project.collaborators = [...project.collaborators, this.assignee!];
          }
          toastPool.insert(
            new Toast(`${this.assignee?.fullName || 'Assignee'} has been added to this project.`),
          );
        }
      }

      this.subscribersIds = data.subscribersIds;

      this.rerenderGroupingViewIfRequired(values, currentTask);
    } catch (e) {
      merge(this, currentTask);

      catchError(e);
    }
  }

  private rerenderGroupingViewIfRequired(values: Partial<Omit<this, '_id'>>, currentTask: Task) {
    const shouldRerenderDueToAssignee =
      values.assigneeId !== undefined &&
      currentTask.assigneeId !== values.assigneeId &&
      taskGroupingStore.groupBy === 'assigneeId';

    const shouldRerenderDueToStatus =
      values.status !== undefined &&
      currentTask.status !== values.status &&
      taskGroupingStore.groupBy === 'status';

    const shouldRerenderDueToPriority =
      values.priority !== undefined &&
      currentTask.priority !== values.priority &&
      taskGroupingStore.groupBy === 'priority';

    if (shouldRerenderDueToAssignee || shouldRerenderDueToStatus || shouldRerenderDueToPriority) {
      taskGroupingStore.triggerRender();
    }
  }

  static async create(input: SaveTaskDto): Promise<Task | undefined> {
    try {
      const { data } = await api.post(`/tasks`, input);

      entityPool.insert(Object.assign(new Task(), { ...data }));

      if (data.projectId) {
        const project = Project.getOne(data.projectId);
        if (project) project.lastUpdatedAt = new Date().toString();
      }

      return data;
    } catch (e) {
      catchError(e);
    }
  }

  async delete() {
    super.delete();

    if (this.projectId) {
      const project = Project.getOne(this.projectId);
      if (project) project.lastUpdatedAt = new Date().toString();
    }
  }

  static fetchAll = async (input?: Partial<GetTasksDto>) => {
    const { role, status, priority } = input || {};

    const params = {
      ...input,
      role: role?.join(','),
      status: status?.join(','),
      priority: priority?.join(','),
    };

    const { data } = await api.get(`/tasks`, { params });

    data.forEach((task: Task) => {
      entityPool.insert(Object.assign(new Task(), task));
    });

    return data;
  };

  static fetchByProject = async (projectId: string) => {
    const { data } = await api.get(`/projects/${projectId}/tasks`);

    data.forEach((task: Task) => {
      entityPool.insert(Object.assign(new Task(), task));
    });

    return data;
  };

  static fetchBySpace = async (spaceId: string) => {
    const { data } = await api.get(`/spaces/${spaceId}/tasks`);

    data.forEach((task: Task) => {
      entityPool.insert(Object.assign(new Task(), task));
    });

    return data;
  };

  async updateTags(tagsIds: string[]) {
    const { data } = await api.patch(`/tasks/${this._id}`, { tagsIds });

    this.tagsIds = data.tagsIds;

    if (taskGroupingStore.groupBy === 'tag') {
      taskGroupingStore.triggerRender();
    }
  }

  async addComment(input: any): Promise<Comment> {
    const { data } = await api.post(`tasks/${this._id}/comments`, input);

    const comment = Object.assign(new Comment(), data);
    comment.isAuthor = true;

    entityPool.insert(comment);

    /**
     * Add author to subscribers (authorId is now memberId after FUP-12)
     */
    if (!this.subscribers.find((subscriber) => subscriber._id === comment.authorId)) {
      const currentMember = context.space.members.find(
        (member) => member._id === comment.authorId,
      );
      if (currentMember) {
        this.subscribersIds = this.subscribersIds.concat(currentMember._id);
      }
    }

    return comment;
  }

  async fetchComments() {
    await Comment.fetchComments(this.$name, this._id);
  }

  async toggleSubscriber(subscriberId: string) {
    if (this.subscribersIds.includes(subscriberId)) {
      return this.removeSubscriber(subscriberId);
    } else {
      return this.addSubscriber(subscriberId);
    }
  }

  async addSubscriber(subscriberId: string) {
    this.subscribersIds = this.subscribersIds.concat(subscriberId);

    try {
      await api.post(`/tasks/${this._id}/subscribers`, {
        subscriberId,
      });
    } catch (e) {
      this.subscribersIds = this.subscribersIds.filter((id) => id !== subscriberId);
      toastPool.insert(new Toast('Failed to add subscriber.', 'error'));
    }
  }

  async removeSubscriber(subscriberId: string) {
    this.subscribersIds = this.subscribersIds.filter((id) => id !== subscriberId);

    try {
      await api.delete(`/tasks/${this._id}/subscribers/${subscriberId}`);
    } catch (e) {
      this.subscribersIds = this.subscribersIds.concat(subscriberId);
      toastPool.insert(new Toast('Failed to remove subscriber.', 'error'));
    }
  }

  toPOJO(): Record<string, any> {
    return {
      _id: this._id,
      spaceId: this.spaceId,
      creatorId: this.creatorId,
      projectId: this.projectId,
      status: this.status,
      dueDate: this.dueDate,
      title: this.title,
      priority: this.priority,
      stepId: this.stepId,
      assigneeId: this.assigneeId,
      description: this.description,
      createdAt: this.createdAt,
      updatedAt: this.updatedAt,
    };
  }
}
